import { Component } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import {
	BillingContract,
	convertWebBillingContractToApiFormat,
	emptyContractSettingsFields,
} from 'src/app/models/billingContractAndOptions';
import { ApiService } from 'src/app/services/api.services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import { AccessHorizontalStepperLabelsComponent } from '../../../access-horizontal-stepper/access-horizontal-stepper-labels.component';
import { NgClass } from '@angular/common';
import { BillingAddContractComponent } from '../billing-add-contract/billing-add-contract.component';
import { BillingEditContractComponent } from '../billing-edit-contract/billing-edit-contract.component';
import { BillingConfirmContractComponent } from '../billing-confirm-contract/billing-confirm-contract.component';

@Component({
	selector: 'app-billing-contract',
	templateUrl: './billing-contract.component.html',
	styleUrls: ['./billing-contract.component.scss'],
	imports: [
		AppNavigationHeaderComponent,
		AccessHorizontalStepperLabelsComponent,
		NgClass,
		BillingAddContractComponent,
		BillingEditContractComponent,
		BillingConfirmContractComponent,
	],
})
export class BillingContractComponent {
	billingAddContractBreadcrumbLabel = $localize`:@@billingContract-billingAddContractBreadcrumbLabel:Billing`;
	steps: string[] = [
		$localize`:@@billingContract-about:About`,
		$localize`:@@billingContract-review:Review`,
		$localize`:@@billingContract-confirmation:Confirmation`,
	];
	currentStep: number = 0;
	enableContinueButton: boolean = false;

	savedAddContractFormGroup?: FormGroup;
	savedAddOnBillingFormGroup?: FormGroup;
	savedBillingContract?: BillingContract;
	contractSuccessfullySaved: boolean = false;

	constructor(
		private readonly apiService: ApiService,
		private readonly snackbar: MatSnackBar
	) {}

	// When form has changes, capture those changes with the below function
	continueContractRegistration(event: any) {
		// Form isn't valid
		if (!event.addContractFormGroup || !event.addOnBillingFormGroup) {
			this.enableContinueButton = false;
			return;
		}

		// Form is valid and enable the button
		this.savedAddContractFormGroup = event.addContractFormGroup;
		this.savedAddOnBillingFormGroup = event.addOnBillingFormGroup;
		this.enableContinueButton = true;
	}

	async onPressContinue() {
		if (this.enableContinueButton) {
			if (this.currentStep === 0) {
				this.savedBillingContract = {
					company: {
						name: this.savedAddContractFormGroup?.controls.company.value.name,
						id: this.savedAddContractFormGroup?.controls.company.value.id,
						active:
							this.savedAddContractFormGroup?.controls.company.value.active,
					},
					netsuiteCustomerInternalId:
						this.savedAddContractFormGroup?.controls.netsuiteCustomerInternalId.value.trim(),
					contractName:
						this.savedAddContractFormGroup?.controls.contractName.value.trim(),
					contractId:
						this.savedAddContractFormGroup?.controls.contractId.value.trim(),
					unitOfMeassureTypeId:
						this.savedAddContractFormGroup?.controls.unitOfMeasure.value,
					currencyTypeId:
						this.savedAddContractFormGroup?.controls.currencyType.value,
					unitPrice: +Number(
						this.savedAddContractFormGroup?.controls.unitPrice.value
					).toFixed(2),
					billingStartDate:
						this.savedAddContractFormGroup?.controls.billingStartDate.value,
					billingTriggerTypeId:
						this.savedAddContractFormGroup?.controls.billingTrigger.value,
					duration: Number(
						this.savedAddContractFormGroup?.controls.durationInMonths.value
					),
					annualIncreasePercent: this.savedAddContractFormGroup?.controls
						.annualIncToggle
						? Number(
								this.savedAddContractFormGroup?.controls.annualIncAdjustmentRate
									.value
							)
						: undefined,
					areDevicesLeased:
						this.savedAddContractFormGroup?.controls.deviceLeaseToggle.value,
					isMonthlyAutoRenewed:
						this.savedAddContractFormGroup?.controls.autoRenewToggle.value,
					addOnBilling: [],
				};
				if (
					(
						this.savedAddOnBillingFormGroup?.controls
							.addOnBillingFormArray as FormArray
					)?.controls
				) {
					for (const group of (
						this.savedAddOnBillingFormGroup?.controls
							.addOnBillingFormArray as FormArray
					)?.controls) {
						if (group instanceof FormGroup) {
							if (group.valid) {
								this.savedBillingContract.addOnBilling.push({
									description: group.controls.description.value.trim(),
									quantity: Number(group.controls.quantity.value),
									unitPrice: +Number(group.controls.unitPrice.value).toFixed(2),
									recurringChargeTypeId:
										group.controls.recurringChargeTypeId.value,
								});
							}
						}
					}
				}
				this.currentStep++;
			} else if (this.currentStep === 1) {
				// Send the contract to the api
				await this.saveBillingContract();
			}
		}
	}

	onPressBack() {
		this.currentStep--;
	}

	async saveBillingContract() {
		const apiBillingForm = convertWebBillingContractToApiFormat(
			this.savedBillingContract!
		);

		await this.apiService
			.saveBillingContract(apiBillingForm)
			.then(() => {
				this.currentStep++;
				this.snackbar.open(
					$localize`:@@billingContract-newContractSaved:Successfully saved new contract.`,
					undefined,
					{
						duration: 2000,
					}
				);
				this.contractSuccessfullySaved = true;
			})
			.catch((_) => {
				this.snackbar.open(
					$localize`:@@billingContract-newContractSaveError:There was a problem generating your contract. Please try again.`,
					undefined,
					{
						duration: 2000,
					}
				);
			});
	}
}
